import React, { FC, FormEvent, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import {
  DropdownField,
  InputDateField,
  Button,
  InputTextArea,
} from '@/components';
import { useTranslation } from 'react-i18next';
import { Divider } from 'primereact/divider';
import { getReleaseStatusList } from '@/services/getReleaseStatusList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { useMutation, useQuery } from '@tanstack/react-query';
import { updateMultipleContainerReleaseStatus } from '@/services/updateMultipleContainerReleaseStates';
import { useToastNotification } from '@/hooks';
import styles from './EditSelectedModal.module.scss';
import { ContainerImportReleaseRegister } from '../types';

type EditSelectedModalProps = {
  selectedItems: ContainerImportReleaseRegister[];
  toggleComponent: React.ReactNode;
  disabled?: boolean;
  totalContainers: number;
  onSuccess: () => void;
};

interface EditSelectedErrors {
  selectedStatus: string;
  releasedUntilDate: string;
  receiveUntilDate: string;
  internObservations: string;
  documentsObservation: string;
}

const EditSelectedModal: FC<EditSelectedModalProps> = ({
  selectedItems,
  totalContainers,
  toggleComponent,
  disabled = false,
  onSuccess,
}) => {
  const [isDialogShown, setIsDialogShown] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>('');
  const [releasedUntilDate, setReleasedUntilDate] = useState<Date | null>();
  const [receiveUntilDate, setReceiveUntilDate] = useState<Date | null>();
  const [internObservations, setInternObservations] = useState<string>('');
  const [documentObservations, setDocumentObservations] = useState<string>('');
  const [formErrors, setFormErrors] = useState<EditSelectedErrors>({
    selectedStatus: '',
    receiveUntilDate: '',
    releasedUntilDate: '',
    internObservations: '',
    documentsObservation: '',
  });
  const { t } = useTranslation('containerRelease');
  const { showToast } = useToastNotification();

  const toggleDialog = () => {
    setIsDialogShown(!isDialogShown);
    setSelectedStatus('');
    setReleasedUntilDate(null);
    setReceiveUntilDate(null);
    setInternObservations('');
    setDocumentObservations('');
    setFormErrors({
      selectedStatus: '',
      receiveUntilDate: '',
      releasedUntilDate: '',
      internObservations: '',
      documentsObservation: '',
    });
  };

  const { data: statusList, isLoading: statusLoading } = useQuery({
    queryKey: ['estadoListDropDown'],
    queryFn: getReleaseStatusList,
  });

  const updateMultipleReleaseStatusesMutation = useMutation({
    mutationFn: updateMultipleContainerReleaseStatus,
    onSuccess: () => {
      showToast({
        severity: 'success',
        detail: t('updateReleaseStatusSuccess'),
      });
      setIsDialogShown(false);
      onSuccess();
    },
    onError: () => {
      showToast({
        severity: 'error',
        detail: t('unexpectedError'),
      });
      setIsDialogShown(false);
    },
  });

  const statusListDropDownOptions = statusList?.map((item) => ({
    name: item,
    value: item,
  }));

  const dialogHeader = <div className="text-blue-800">{t('modalTitle')}</div>;

  const oids: number[] = selectedItems?.map((item) => item.oidImport);

  const validateForm = () => {
    const errors: EditSelectedErrors = { ...formErrors };
    let isValid = true;

    if (!selectedStatus) {
      errors.selectedStatus = t('statusFieldRequired');
      isValid = false;
    }
    if (!releasedUntilDate) {
      errors.releasedUntilDate = t('releasedUntilDateFieldRequired');
      isValid = false;
    }
    if (!receiveUntilDate) {
      errors.receiveUntilDate = t('receiveUntilDateFieldRequired');
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmitform = (e: FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      updateMultipleReleaseStatusesMutation.mutate({
        oidImport: oids,
        estado: selectedStatus,
        liberadoHasta: releasedUntilDate ?? new Date(),
        recibirHasta: receiveUntilDate ?? new Date(),
        observaciones: internObservations,
        observacionesComprobante: documentObservations,
      });
    }
  };

  const handleStatusDropdownChange = (e: { value: string | null }) => {
    setSelectedStatus(e?.value);
  };

  return (
    <>
      <div
        onClick={disabled ? undefined : toggleDialog}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            toggleDialog();
          }
        }}
        role="button"
        tabIndex={0}
        className={styles.toggleComponentContainer}
      >
        {toggleComponent}
      </div>

      <Dialog
        header={dialogHeader}
        visible={isDialogShown}
        className={classNames(styles.dialog)}
        onHide={toggleDialog}
        modal
        breakpoints={{ '1200px': '480px', '769px': '327px' }}
      >
        <div className="flex flex-column pt-1 pl-3 pb-3 pr-3 text-left">
          <p className={styles.description}>
            {t('modalDescriptionText')}{' '}
            <b>{`${selectedItems?.length}/${totalContainers}`} </b>
            {t('modalDescriptionText2')}
          </p>
          <div className="flex flex-column mt-2">
            <form onSubmit={handleSubmitform}>
              <div className="flex">
                {' '}
                <DropdownField
                  label={t('statusField')}
                  disabled={statusLoading}
                  options={statusListDropDownOptions}
                  placeholder={t('statusPlaceholder')}
                  onChange={(e) => handleStatusDropdownChange(e)}
                  value={selectedStatus}
                  required
                  id="selectedStatus"
                  error={formErrors.selectedStatus}
                  className={classNames(styles.inputContainer)}
                />
              </div>
              <div
                className={classNames(
                  styles.formSpacing,
                  'flex flex-row mt--10'
                )}
              >
                <InputDateField
                  value={releasedUntilDate}
                  id="releasedUntilDate"
                  name="releasedUntilDate"
                  label={t('releasedUntilDateField')}
                  className={styles.dateField}
                  readOnlyInput
                  onChange={(e) => setReleasedUntilDate(e)}
                  placeholder={t('datePickerPlaceholder')}
                  error={formErrors.releasedUntilDate}
                />
                <InputDateField
                  value={receiveUntilDate}
                  id="receiveUntilDate"
                  name="endOpereceiveUntilDate"
                  label={t('receiveUntilDateField')}
                  className={styles.dateField}
                  readOnlyInput
                  onChange={(e) => setReceiveUntilDate(e)}
                  placeholder={t('datePickerPlaceholder')}
                  error={formErrors.receiveUntilDate}
                />
              </div>
              <div
                className={classNames(styles.fieldSeparation, 'flex flex-row')}
              >
                <InputTextArea
                  value={internObservations ?? ''}
                  label={t('internObservationsField')}
                  id="internObservations"
                  name="internObservations"
                  placeholder={t('internObservationsField')}
                  onChange={(e) => setInternObservations(e.target.value)}
                  error={formErrors.internObservations}
                  inputClassName={styles.textArea}
                />
              </div>
              <div
                className={classNames(styles.fieldSeparation, 'flex flex-row')}
              >
                <InputTextArea
                  value={documentObservations ?? ''}
                  label={t('documentObservationsField')}
                  id="documentObservations"
                  name="documentObservations"
                  placeholder={t('documentObservationsField')}
                  onChange={(e) => setDocumentObservations(e.target.value)}
                  error={formErrors.documentsObservation}
                  inputClassName={styles.textArea}
                />
              </div>
              <div className="w-full">
                <Divider className={classNames(styles.divider)} />
              </div>
              <div className={classNames(styles.modalFooter)}>
                <div className="flex">
                  <button
                    onClick={toggleDialog}
                    type="button"
                    className={`${styles.cancelButton} mr-4`}
                  >
                    {t('cancelButton')}
                  </button>
                  <Button
                    loading={updateMultipleReleaseStatusesMutation.isPending}
                    type="submit"
                    className={`${styles.saveButton}`}
                    green
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2 ml-2" />
                    {t('saveChangesButton')}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default EditSelectedModal;
